import React, { useCallback, useEffect, useState } from "react";
import {
  fetchRecoverableGroupOrders, fetchGroupOrderRecoveryItems, createFoodRecovery,
  fetchFoodRecovery, reuseFoodRecovery, deleteFoodRecovery, fetchFoodRecoveryReport,
} from "../services/api";

// Food Recovery (event/group only). Records prepared food recovered from a
// banquet/group order as REUSABLE servings (a pool the kitchen can re-serve
// later at zero cost = recovered profit) plus optional WASTE. Owner-side value
// tracking — it never changes the event's cost.

const S = {
  en: {
    title: "Food Recovery", sub: "Recover reusable food from events — track the extra value it brings back. Event/group orders only; the event's cost is never changed.",
    rec_recovered: "Reusable recovered", rec_pool: "Still in pool", rec_realized: "Profit recovered", rec_potential: "Potential (if re-served)", rec_waste: "Waste logged",
    servings: "servings", record: "Record recovery", pick_event: "Choose an event / group order", none_events: "No event/group orders yet.",
    item: "Item", category: "Type", reusable: "Reusable", waste: "Waste", add_row: "+ Add item", save: "Save recovery", saving: "Saving…",
    hint_buffet: "Buffet? Split it into items (e.g. Kitfo, Vegetables) — recovery differs by item.",
    pool_title: "Recovery pool", show_waste: "Show waste", remaining: "left", reuse: "Re-serve", reuse_q: "How many servings re-served?", per_serving: "/serving",
    empty_pool: "Nothing recorded yet.", del: "Remove", from_event: "from", price: "Price", cost: "Cost", none_price: "Set a price so profit can be calculated.",
    realized_note: "Re-served food carries zero cost (already spent at the event), so its full price is recovered profit.",
  },
  am: {
    title: "የምግብ ማገገሚያ", sub: "ከዝግጅቶች እንደገና ሊቀርብ የሚችል ምግብ ያግኙ — የሚመልሰውን ተጨማሪ ዋጋ ይከታተሉ። ለዝግጅት/ቡድን ትዕዛዞች ብቻ፤ የዝግጅቱ ወጪ አይቀየርም።",
    rec_recovered: "ሊገለገል የተመለሰ", rec_pool: "በክምችት ውስጥ", rec_realized: "የተገኘ ትርፍ", rec_potential: "እምቅ (እንደገና ቢቀርብ)", rec_waste: "የተመዘገበ ብክነት",
    servings: "አገልግሎቶች", record: "ማገገም መዝግብ", pick_event: "ዝግጅት / የቡድን ትዕዛዝ ይምረጡ", none_events: "እስካሁን ዝግጅት የለም።",
    item: "እቃ", category: "አይነት", reusable: "ሊገለገል የሚችል", waste: "ብክነት", add_row: "+ እቃ ጨምር", save: "ማገገም አስቀምጥ", saving: "በማስቀመጥ ላይ…",
    hint_buffet: "ቡፌ ነው? ወደ እቃዎች ይክፈሉት (ለምሳሌ ክትፎ፣ አትክልት) — ማገገም በእቃ ይለያያል።",
    pool_title: "የማገገሚያ ክምችት", show_waste: "ብክነት አሳይ", remaining: "ቀሪ", reuse: "እንደገና አቅርብ", reuse_q: "ስንት አገልግሎቶች እንደገና ቀረቡ?", per_serving: "/አገልግሎት",
    empty_pool: "እስካሁን የተመዘገበ የለም።", del: "አስወግድ", from_event: "ከ", price: "ዋጋ", cost: "ወጪ", none_price: "ትርፍ ለማስላት ዋጋ ያስገቡ።",
    realized_note: "እንደገና የቀረበ ምግብ ዜሮ ወጪ አለው (ቀድሞ በዝግጅቱ ወጪ ስለተደረገ)፣ ስለዚህ ሙሉ ዋጋው የተገኘ ትርፍ ነው።",
  },
  om: {
    title: "Deebisa Nyaataa", sub: "Sagantaa irraa nyaata irra deebi'ee tajaajiluu danda'u argadhu — gatii dabalataa deebisu hordofi. Ajaja sagantaa/garee qofaaf; gatiin sagantaa hin jijjiiramu.",
    rec_recovered: "Irra deebi'ee argame", rec_pool: "Kuusaa keessa jiru", rec_realized: "Bu'aa argame", rec_potential: "Dandeettii (yoo irra deebi'e)", rec_waste: "Balleessaa galmaa'e",
    servings: "tajaajila", record: "Deebisa galmeessi", pick_event: "Sagantaa / ajaja garee filadhu", none_events: "Ammatti sagantaan hin jiru.",
    item: "Meeshaa", category: "Gosa", reusable: "Irra deebi'ee kan tajaajilu", waste: "Balleessaa", add_row: "+ Meeshaa dabali", save: "Deebisa olkaa'i", saving: "Olkaa'aa jira…",
    hint_buffet: "Bufee? Gara meeshaaleetti qoodi (fkn Kitfoo, Kuduraa) — deebisni meeshaadhaan garaagara.",
    pool_title: "Kuusaa deebisaa", show_waste: "Balleessaa agarsiisi", remaining: "hafe", reuse: "Irra deebi'ii dhiheessi", reuse_q: "Tajaajila meeqatu irra deebi'ee dhihaate?", per_serving: "/tajaajila",
    empty_pool: "Ammatti wanti galmaa'e hin jiru.", del: "Balleessi", from_event: "irraa", price: "Gatii", cost: "Baasii", none_price: "Bu'aa shallaguuf gatii galchi.",
    realized_note: "Nyaanni irra deebi'ee dhihaatu baasii duwwaa qaba (duraan sagantaa irratti baasii ta'eera), kanaafuu gatiin isaa guutuun bu'aa argamedha.",
  },
};

export default function FoodRecoveryPage() {
  const lang = (typeof window !== "undefined" && localStorage.getItem("lang")) || "en";
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const money = (n) => `Br ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const [report, setReport] = useState(null);
  const [pool, setPool] = useState([]);
  const [events, setEvents] = useState([]);
  const [showWaste, setShowWaste] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // record form
  const [eventId, setEventId] = useState("");
  const [rows, setRows] = useState([]);   // {menu_id, item_name, category, servings, unit_price, unit_cost}

  const load = useCallback(async () => {
    setErr("");
    try {
      const [rep, list, evs] = await Promise.all([
        fetchFoodRecoveryReport({ days: 30 }),
        fetchFoodRecovery({ days: 90 }),
        fetchRecoverableGroupOrders(),
      ]);
      setReport(rep); setPool(list); setEvents(evs);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not load Food Recovery."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickEvent = async (id) => {
    setEventId(id);
    if (!id) { setRows([]); return; }
    try {
      const items = await fetchGroupOrderRecoveryItems(id);
      setRows(items.map((it) => ({
        menu_id: it.menu_id, item_name: it.item_name, category: "reusable",
        servings: "", unit_price: it.unit_price, unit_cost: it.unit_cost,
      })));
    } catch { setRows([]); }
  };

  const setRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((rs) => [...rs, { menu_id: null, item_name: "", category: "reusable", servings: "", unit_price: "", unit_cost: "" }]);
  const rmRow = (i) => setRows((rs) => rs.filter((_, j) => j !== i));

  const save = async () => {
    const items = rows
      .filter((r) => (r.item_name || "").trim() && Number(r.servings) > 0)
      .map((r) => ({
        menu_id: r.menu_id || null, item_name: r.item_name.trim(), category: r.category,
        servings: Number(r.servings),
        unit_price: r.unit_price === "" ? null : Number(r.unit_price),
        unit_cost: r.unit_cost === "" ? null : Number(r.unit_cost),
      }));
    if (!items.length) { setErr("Add at least one item with servings."); return; }
    setBusy(true); setErr("");
    try {
      await createFoodRecovery({ group_order_id: eventId ? Number(eventId) : null, items });
      setEventId(""); setRows([]);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const doReuse = async (row) => {
    const raw = window.prompt(`${t("reuse_q")} (${row.servings_remaining} ${t("remaining")})`, String(row.servings_remaining));
    if (raw == null) return;
    const n = Number(raw);
    if (!(n > 0)) return;
    try { await reuseFoodRecovery(row.id, n); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not re-serve."); }
  };

  const del = async (row) => {
    if (!window.confirm(`${t("del")}?`)) return;
    try { await deleteFoodRecovery(row.id); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not remove."); }
  };

  const eventName = (id) => { const e = events.find((x) => x.id === id); return e ? e.name : ""; };
  const reusable = pool.filter((p) => p.category === "reusable");
  const waste = pool.filter((p) => p.category === "waste");

  const card = { flex: "1 1 150px", background: "#fff", border: "1px solid #eef0f3", borderRadius: 10, padding: "12px 14px" };
  const cardNum = { fontSize: 22, fontWeight: 700, marginTop: 2 };

  return (
    <div className="page">
      <h1 style={{ marginBottom: 4 }}>♻️ {t("title")}</h1>
      <p className="muted" style={{ marginTop: 0, maxWidth: 720 }}>{t("sub")}</p>

      {err && <div style={{ background: "#fdecec", color: "#b02a2a", padding: "8px 12px", borderRadius: 8, margin: "8px 0" }}>{err}</div>}

      {/* Summary cards */}
      {report && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0 20px" }}>
          <div style={card}><div className="muted" style={{ fontSize: 12 }}>{t("rec_recovered")}</div><div style={cardNum}>{report.reusable_servings}</div><div className="muted" style={{ fontSize: 11 }}>{t("servings")}</div></div>
          <div style={card}><div className="muted" style={{ fontSize: 12 }}>{t("rec_pool")}</div><div style={cardNum}>{report.pool_servings}</div><div className="muted" style={{ fontSize: 11 }}>{t("servings")}</div></div>
          <div style={{ ...card, background: "#f2fbf4", borderColor: "#cdeed5" }}><div className="muted" style={{ fontSize: 12 }}>{t("rec_realized")}</div><div style={{ ...cardNum, color: "#2b8a3e" }}>{money(report.realized_profit)}</div></div>
          <div style={card}><div className="muted" style={{ fontSize: 12 }}>{t("rec_potential")}</div><div style={cardNum}>{money(report.potential_value)}</div></div>
          <div style={card}><div className="muted" style={{ fontSize: 12 }}>{t("rec_waste")}</div><div style={cardNum}>{report.waste_servings}</div><div className="muted" style={{ fontSize: 11 }}>{t("servings")}</div></div>
        </div>
      )}

      {/* Record form */}
      <div style={{ background: "#fff", border: "1px solid #eef0f3", borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>{t("record")}</h3>
        <label className="muted" style={{ fontSize: 13 }}>{t("pick_event")}</label>
        <select value={eventId} onChange={(e) => pickEvent(e.target.value)} style={{ display: "block", width: "100%", maxWidth: 420, padding: 8, margin: "4px 0 6px" }}>
          <option value="">—</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}{e.headcount ? ` · ${e.headcount}p` : ""}</option>)}
        </select>
        {!events.length && <div className="muted" style={{ fontSize: 12 }}>{t("none_events")}</div>}
        <div className="muted" style={{ fontSize: 12, margin: "6px 0" }}>{t("hint_buffet")}</div>

        {rows.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
            <thead><tr style={{ textAlign: "left", fontSize: 12, color: "#7f8fa0" }}>
              <th style={{ padding: 4 }}>{t("item")}</th><th style={{ padding: 4 }}>{t("category")}</th>
              <th style={{ padding: 4, width: 90 }}>{t("servings")}</th><th style={{ padding: 4, width: 90 }}>{t("price")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ padding: 4 }}><input value={r.item_name} onChange={(e) => setRow(i, "item_name", e.target.value)} style={{ width: "100%", padding: 6 }} /></td>
                  <td style={{ padding: 4 }}>
                    <select value={r.category} onChange={(e) => setRow(i, "category", e.target.value)} style={{ padding: 6 }}>
                      <option value="reusable">{t("reusable")}</option>
                      <option value="waste">{t("waste")}</option>
                    </select>
                  </td>
                  <td style={{ padding: 4 }}><input type="number" min="0" value={r.servings} onChange={(e) => setRow(i, "servings", e.target.value)} style={{ width: "100%", padding: 6 }} /></td>
                  <td style={{ padding: 4 }}><input type="number" min="0" value={r.unit_price} onChange={(e) => setRow(i, "unit_price", e.target.value)} style={{ width: "100%", padding: 6 }} /></td>
                  <td style={{ padding: 4 }}><button className="btn" onClick={() => rmRow(i)} style={{ padding: "4px 8px" }}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
          <button className="btn" onClick={addRow}>{t("add_row")}</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !rows.length}>{busy ? t("saving") : t("save")}</button>
        </div>
      </div>

      {/* Pool */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: "0 0 6px" }}>{t("pool_title")}</h3>
        <label className="muted" style={{ fontSize: 13 }}><input type="checkbox" checked={showWaste} onChange={(e) => setShowWaste(e.target.checked)} /> {t("show_waste")}</label>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>{t("realized_note")}</p>

      {reusable.length === 0 && (!showWaste || waste.length === 0) && <div className="muted">{t("empty_pool")}</div>}

      {reusable.map((r) => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: "1px solid #eef0f3", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
          <div style={{ flex: "1 1 200px" }}>
            <strong>{r.item_name}</strong>{r.group_order_id ? <span className="muted" style={{ fontSize: 12 }}> · {t("from_event")} {eventName(r.group_order_id)}</span> : null}
            <div className="muted" style={{ fontSize: 12 }}>{money(r.unit_price)}{t("per_serving")}{r.unit_price ? "" : ` — ${t("none_price")}`}</div>
          </div>
          <div style={{ textAlign: "center" }}><div style={{ fontWeight: 700 }}>{r.servings_remaining}</div><div className="muted" style={{ fontSize: 11 }}>{t("remaining")}</div></div>
          <div style={{ textAlign: "center" }}><div style={{ fontWeight: 700, color: "#2b8a3e" }}>{money(r.realized_profit)}</div><div className="muted" style={{ fontSize: 11 }}>{t("rec_realized")}</div></div>
          <button className="btn btn-primary" onClick={() => doReuse(r)} disabled={r.servings_remaining <= 0} style={{ padding: "6px 10px" }}>{t("reuse")}</button>
          <button className="btn" onClick={() => del(r)} style={{ padding: "6px 10px" }}>{t("del")}</button>
        </div>
      ))}

      {showWaste && waste.map((r) => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fbfbfc", border: "1px dashed #e3e6ea", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
          <div style={{ flex: "1 1 200px" }}><strong>{r.item_name}</strong> <span className="muted" style={{ fontSize: 12 }}>· {t("waste")}</span>{r.reason ? <div className="muted" style={{ fontSize: 12 }}>{r.reason}</div> : null}</div>
          <div style={{ textAlign: "center" }}><div style={{ fontWeight: 700 }}>{r.servings}</div><div className="muted" style={{ fontSize: 11 }}>{t("servings")}</div></div>
          <button className="btn" onClick={() => del(r)} style={{ padding: "6px 10px" }}>{t("del")}</button>
        </div>
      ))}
    </div>
  );
}
